/**
 * Every country the product offers → the locale key that names it. (F34)
 *
 * The journey surfaces used to print `getCountryName(code)` from
 * `@tonnie/foundation`, an ENGLISH table, so a pt-PT reader saw "Brazil". The
 * names live in the catalogue as `common.countries.<ALPHA-2>`.
 *
 * A TYPED MAP OF FULL LITERAL KEYS, not a key built at the call site
 * (`common.countries.` + code): a key assembled at runtime can be
 * checked by nothing — not TypeScript, not `check-key-usage.mjs`. Here every
 * key is a literal the key-usage scan can see, and
 * `dynamic-key-coverage.spec.ts` asserts the map covers EXACTLY
 * `COUNTRIES` (so a country added there without a name here fails the build)
 * and that every key exists in en.json (every other locale follows by the
 * `i18n:check` parity gate).
 *
 * Keys are alpha-2: stored country codes are canonical alpha-2 (migration 0165
 * and `toAlpha2CountryCode` at the writers).
 */
export const COUNTRY_NAME_KEY = {
  AD: 'common.countries.AD',
  AE: 'common.countries.AE',
  AF: 'common.countries.AF',
  AG: 'common.countries.AG',
  AL: 'common.countries.AL',
  AM: 'common.countries.AM',
  AO: 'common.countries.AO',
  AR: 'common.countries.AR',
  AT: 'common.countries.AT',
  AU: 'common.countries.AU',
  AZ: 'common.countries.AZ',
  BA: 'common.countries.BA',
  BB: 'common.countries.BB',
  BD: 'common.countries.BD',
  BE: 'common.countries.BE',
  BF: 'common.countries.BF',
  BG: 'common.countries.BG',
  BH: 'common.countries.BH',
  BI: 'common.countries.BI',
  BJ: 'common.countries.BJ',
  BN: 'common.countries.BN',
  BO: 'common.countries.BO',
  BR: 'common.countries.BR',
  BS: 'common.countries.BS',
  BT: 'common.countries.BT',
  BW: 'common.countries.BW',
  BY: 'common.countries.BY',
  BZ: 'common.countries.BZ',
  CA: 'common.countries.CA',
  CD: 'common.countries.CD',
  CF: 'common.countries.CF',
  CG: 'common.countries.CG',
  CH: 'common.countries.CH',
  CI: 'common.countries.CI',
  CL: 'common.countries.CL',
  CM: 'common.countries.CM',
  CN: 'common.countries.CN',
  CO: 'common.countries.CO',
  CR: 'common.countries.CR',
  CU: 'common.countries.CU',
  CV: 'common.countries.CV',
  CY: 'common.countries.CY',
  CZ: 'common.countries.CZ',
  DE: 'common.countries.DE',
  DJ: 'common.countries.DJ',
  DK: 'common.countries.DK',
  DM: 'common.countries.DM',
  DO: 'common.countries.DO',
  DZ: 'common.countries.DZ',
  EC: 'common.countries.EC',
  EE: 'common.countries.EE',
  EG: 'common.countries.EG',
  ER: 'common.countries.ER',
  ES: 'common.countries.ES',
  ET: 'common.countries.ET',
  FI: 'common.countries.FI',
  FJ: 'common.countries.FJ',
  FM: 'common.countries.FM',
  FR: 'common.countries.FR',
  GA: 'common.countries.GA',
  GB: 'common.countries.GB',
  GD: 'common.countries.GD',
  GE: 'common.countries.GE',
  GH: 'common.countries.GH',
  GM: 'common.countries.GM',
  GN: 'common.countries.GN',
  GQ: 'common.countries.GQ',
  GR: 'common.countries.GR',
  GT: 'common.countries.GT',
  GW: 'common.countries.GW',
  GY: 'common.countries.GY',
  HK: 'common.countries.HK',
  HN: 'common.countries.HN',
  HR: 'common.countries.HR',
  HT: 'common.countries.HT',
  HU: 'common.countries.HU',
  ID: 'common.countries.ID',
  IE: 'common.countries.IE',
  IL: 'common.countries.IL',
  IN: 'common.countries.IN',
  IQ: 'common.countries.IQ',
  IR: 'common.countries.IR',
  IS: 'common.countries.IS',
  IT: 'common.countries.IT',
  JM: 'common.countries.JM',
  JO: 'common.countries.JO',
  JP: 'common.countries.JP',
  KE: 'common.countries.KE',
  KG: 'common.countries.KG',
  KH: 'common.countries.KH',
  KI: 'common.countries.KI',
  KM: 'common.countries.KM',
  KN: 'common.countries.KN',
  KP: 'common.countries.KP',
  KR: 'common.countries.KR',
  KW: 'common.countries.KW',
  KZ: 'common.countries.KZ',
  LA: 'common.countries.LA',
  LB: 'common.countries.LB',
  LC: 'common.countries.LC',
  LI: 'common.countries.LI',
  LK: 'common.countries.LK',
  LR: 'common.countries.LR',
  LS: 'common.countries.LS',
  LT: 'common.countries.LT',
  LU: 'common.countries.LU',
  LV: 'common.countries.LV',
  LY: 'common.countries.LY',
  MA: 'common.countries.MA',
  MC: 'common.countries.MC',
  MD: 'common.countries.MD',
  ME: 'common.countries.ME',
  MG: 'common.countries.MG',
  MH: 'common.countries.MH',
  MK: 'common.countries.MK',
  ML: 'common.countries.ML',
  MM: 'common.countries.MM',
  MN: 'common.countries.MN',
  MO: 'common.countries.MO',
  MR: 'common.countries.MR',
  MT: 'common.countries.MT',
  MU: 'common.countries.MU',
  MV: 'common.countries.MV',
  MW: 'common.countries.MW',
  MX: 'common.countries.MX',
  MY: 'common.countries.MY',
  MZ: 'common.countries.MZ',
  NA: 'common.countries.NA',
  NE: 'common.countries.NE',
  NG: 'common.countries.NG',
  NI: 'common.countries.NI',
  NL: 'common.countries.NL',
  NO: 'common.countries.NO',
  NP: 'common.countries.NP',
  NR: 'common.countries.NR',
  NZ: 'common.countries.NZ',
  OM: 'common.countries.OM',
  PA: 'common.countries.PA',
  PE: 'common.countries.PE',
  PG: 'common.countries.PG',
  PH: 'common.countries.PH',
  PK: 'common.countries.PK',
  PL: 'common.countries.PL',
  PS: 'common.countries.PS',
  PT: 'common.countries.PT',
  PW: 'common.countries.PW',
  PY: 'common.countries.PY',
  QA: 'common.countries.QA',
  RO: 'common.countries.RO',
  RS: 'common.countries.RS',
  RU: 'common.countries.RU',
  RW: 'common.countries.RW',
  SA: 'common.countries.SA',
  SB: 'common.countries.SB',
  SC: 'common.countries.SC',
  SD: 'common.countries.SD',
  SE: 'common.countries.SE',
  SG: 'common.countries.SG',
  SI: 'common.countries.SI',
  SK: 'common.countries.SK',
  SL: 'common.countries.SL',
  SM: 'common.countries.SM',
  SN: 'common.countries.SN',
  SO: 'common.countries.SO',
  SR: 'common.countries.SR',
  SS: 'common.countries.SS',
  ST: 'common.countries.ST',
  SV: 'common.countries.SV',
  SY: 'common.countries.SY',
  SZ: 'common.countries.SZ',
  TD: 'common.countries.TD',
  TG: 'common.countries.TG',
  TH: 'common.countries.TH',
  TJ: 'common.countries.TJ',
  TL: 'common.countries.TL',
  TM: 'common.countries.TM',
  TN: 'common.countries.TN',
  TO: 'common.countries.TO',
  TR: 'common.countries.TR',
  TT: 'common.countries.TT',
  TV: 'common.countries.TV',
  TW: 'common.countries.TW',
  TZ: 'common.countries.TZ',
  UA: 'common.countries.UA',
  UG: 'common.countries.UG',
  US: 'common.countries.US',
  UY: 'common.countries.UY',
  UZ: 'common.countries.UZ',
  VA: 'common.countries.VA',
  VC: 'common.countries.VC',
  VE: 'common.countries.VE',
  VN: 'common.countries.VN',
  VU: 'common.countries.VU',
  WS: 'common.countries.WS',
  YE: 'common.countries.YE',
  ZA: 'common.countries.ZA',
  ZM: 'common.countries.ZM',
  ZW: 'common.countries.ZW',
} as const satisfies Record<string, `common.countries.${string}`>;

/** A country the product offers, by its alpha-2 code. */
export type NamedCountryCode = keyof typeof COUNTRY_NAME_KEY;

/** The locale key naming one offered country. */
export type CountryNameKey = (typeof COUNTRY_NAME_KEY)[NamedCountryCode];

/** Is `code` (already upper-cased) a country the product offers? */
function isNamedCountryCode(code: string): code is NamedCountryCode {
  return Object.prototype.hasOwnProperty.call(COUNTRY_NAME_KEY, code);
}

/**
 * The locale key naming `code`, or `null` when `code` is not a country the
 * product offers. Case-insensitive, because codes arrive from storage and
 * from URL parameters.
 */
export function countryNameKey(code: string): CountryNameKey | null {
  const upper = code.toUpperCase();
  return isNamedCountryCode(upper) ? COUNTRY_NAME_KEY[upper] : null;
}
